package com.evoapps.evoshift.domain.usecase.schedule

import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.schedule.AutoScheduler
import com.evoapps.evoshift.domain.schedule.ContextShift
import com.evoapps.evoshift.domain.schedule.HoursTarget
import com.evoapps.evoshift.domain.schedule.ProposedAssignment
import com.evoapps.evoshift.domain.schedule.ScheduleOptions
import com.evoapps.evoshift.domain.schedule.SchedulePlan
import com.evoapps.evoshift.domain.schedule.ScheduleProblem
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.schedule.ScheduleWarning
import com.evoapps.evoshift.domain.schedule.StaffConstraint
import com.evoapps.evoshift.domain.schedule.StaffProfiles
import com.evoapps.evoshift.domain.schedule.StaffRules
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.schedule.TargetSource
import com.evoapps.evoshift.domain.schedule.WarningKind
import com.evoapps.evoshift.domain.model.RosterStatus
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * Plans the roster's open shifts automatically and returns the preview (nothing is written; see
 * [ApplyAutoScheduleUseCase]). Reads the roster's shifts, the team, their availability, approved
 * absences, pay terms (for hour targets, only where the planner may read them), and the shifts
 * around the range: the previous and next month here, and the people's other workplaces where
 * readable. The rules are described on [AutoScheduler]. SH-221: while the workplace has no
 * published or locked roster yet, people without availability count as available
 * ([ScheduleOptions.firstRoster]).
 */
class PlanAutoScheduleUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val absenceRepository: AbsenceRepository,
    /** SH-47: medical exam and OHS dates or statuses; without it nobody is blocked by them. */
    private val staffRepository: StaffRepository? = null
) {
    companion object {
        /** Days before and after the range whose shifts count for rest and days in a row. */
        const val CONTEXT_DAYS = 7
    }

    /**
     * @param range the days to plan (null: the whole month); cut to the roster's month.
     * @param personalConstraints rule 9 per user id (filled by the manager or an AI step).
     * @param readPayTerms false skips pay terms (targets unknown), e.g. for a non-financial manager.
     */
    suspend operator fun invoke(
        rosterId: String,
        range: ScheduleRange? = null,
        options: ScheduleOptions = ScheduleOptions(),
        staffRules: StaffRules = StaffRules(),
        personalConstraints: Map<String, List<StaffConstraint>> = emptyMap(),
        readPayTerms: Boolean = true
    ): Result<SchedulePlan> = prepare(rosterId, range, options, staffRules, personalConstraints, readPayTerms)
        .mapCatching { it.plan() }

    /**
     * Reads everything and builds the solver's problem without solving it (SH-90: the AI step
     * pseudonymises this same problem, then [PreparedSchedule.plan] checks its proposals).
     */
    suspend fun prepare(
        rosterId: String,
        range: ScheduleRange? = null,
        options: ScheduleOptions = ScheduleOptions(),
        staffRules: StaffRules = StaffRules(),
        personalConstraints: Map<String, List<StaffConstraint>> = emptyMap(),
        readPayTerms: Boolean = true
    ): Result<PreparedSchedule> = runCatching {
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        val workplace = workplaceRepository.getWorkplace(roster.workplaceId).getOrThrow()
        val settings = workplace.settings
        val monthStart = LocalDate(roster.year, roster.month, 1)
        val monthEnd = monthStart.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1))
        val requested = range ?: ScheduleRange(monthStart, monthEnd)
        require(requested.start <= monthEnd && requested.endInclusive >= monthStart) { "The range is outside the roster's month" }
        val planned = ScheduleRange(maxOf(requested.start, monthStart), minOf(requested.endInclusive, monthEnd))
        val warnings = mutableListOf<ScheduleWarning>()
        if (planned != requested) warnings += ScheduleWarning(WarningKind.RANGE_CLAMPED)

        val shifts = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val team = userRepository.getAssignableEmployees(roster.workplaceId).getOrThrow().sortedBy { it.uid }
        val month = roster.getMonthYear()

        val submitted = availabilityRepository.getAvailabilityForRoster(rosterId).getOrDefault(emptyList())
            .filter { it.workplaceId == null || it.workplaceId == roster.workplaceId }
            .associateBy { it.userId }
        val availability: Map<String, Availability?> = team.associate { user ->
            user.uid to (submitted[user.uid] ?: availabilityRepository.getAvailability(user.uid, roster.workplaceId, month).getOrNull())
        }
        val absences = absenceRepository.getAbsencesForRoster(roster.workplaceId, monthStart.toString(), monthEnd.toString())
            .getOrDefault(emptyList()).filter { it.status == AbsenceStatus.APPROVED }

        // Hour targets need pay terms: financial managers and the owner may read them; others not.
        val nominalHours = PayrollRules.of(settings.market).workingHours(roster.year, roster.month)
        val unreadablePay = mutableListOf<String>()
        val terms = team.associate { user ->
            if (!readPayTerms) return@associate user.uid to (HoursTarget.unknown(TargetSource.NOT_READ) to SystemContractType.NOT_SET)
            val sensitive = userRepository.getSensitiveUserData(user.uid)
            if (sensitive.isFailure) unreadablePay += user.uid
            val pay = sensitive.getOrNull()?.financialEngagements?.get(roster.workplaceId)
            val target = StaffProfiles.monthlyTarget(pay, payTermsReadable = sensitive.isSuccess, nominalHours = nominalHours)
            user.uid to (target to (pay?.contract ?: SystemContractType.NOT_SET))
        }
        if (unreadablePay.isNotEmpty()) warnings += ScheduleWarning(WarningKind.PAY_TERMS_UNREADABLE, userIds = unreadablePay)

        // SH-47: the dates where the planner may read them (owners, financial managers), else the
        // statuses operational managers see; like AssignUserToShiftUseCase's check.
        val readiness: Map<String, StaffReadiness> = staffRepository?.let { repo ->
            var statuses: List<StaffReadiness>? = null
            team.mapNotNull { user ->
                val record = if (readPayTerms) repo.getStaffRecord(user.uid, roster.workplaceId).getOrNull() else null
                val known = record?.let { StaffChecks.readiness(it, user.uid, roster.workplaceId, monthStart) }
                    ?: (statuses ?: repo.getReadiness(roster.workplaceId).getOrDefault(emptyList()).also { statuses = it })
                        .firstOrNull { it.userId == user.uid }
                known?.let { user.uid to it }
            }.toMap()
        }.orEmpty()
        val leadRule = ShiftLeadRule.of(workplace)
        val staff = team.map { user ->
            val (target, contract) = terms.getValue(user.uid)
            StaffProfiles.build(
                user = user, workplaceId = roster.workplaceId, workplaceOwnerId = workplace.ownerId,
                year = roster.year, month = roster.month, target = target, contract = contract,
                availability = availability[user.uid], absences = absences, rules = staffRules, leadRule = leadRule,
                constraints = personalConstraints[user.uid].orEmpty(),
                readiness = readiness[user.uid]
            )
        }

        // Around the range: this workplace's neighbouring months, then the people's other workplaces.
        val from = planned.start.plus(DatePeriod(days = -CONTEXT_DAYS)).toString()
        val to = planned.endInclusive.plus(DatePeriod(days = CONTEXT_DAYS)).toString()
        val teamIds = team.map { it.uid }.toSet()
        fun Shift.inWindow() = userId in teamIds && date >= from && date <= to
        val context = mutableListOf<ContextShift>()
        RosterIds.monthsBetween(from, to)
            .map { (year, m) -> RosterIds.of(roster.workplaceId, year, m) }
            .filter { it != rosterId }
            .forEach { neighbour ->
                shiftRepository.getShiftsForRoster(neighbour).getOrDefault(emptyList()).filter { it.inWindow() }
                    .forEach { context += ContextShift(it.userId!!, it.date, it.plannedTime, atThisWorkplace = true) }
            }
        // SH-135: the people's shifts at their other workplaces come from the server (the same
        // owner's drafts too; another employer's only once published, times only).
        val withOtherWorkplaces = team.filter { user ->
            user.engagements.values.any { it.isActive && it.workplaceId != roster.workplaceId }
        }.map { it.uid }
        if (withOtherWorkplaces.isNotEmpty()) {
            shiftRepository.findBusyElsewhere(roster.workplaceId, withOtherWorkplaces, from, to)
                .onSuccess { busy ->
                    busy.filter { it.userId in teamIds }
                        .forEach { context += ContextShift(it.userId, it.date, it.plannedTime, atThisWorkplace = false) }
                }
                .onFailure { warnings += ScheduleWarning(WarningKind.OTHER_WORKPLACES_UNREADABLE, userIds = withOtherWorkplaces) }
        }

        val problem = ScheduleProblem(
            rosterId = rosterId, year = roster.year, month = roster.month, range = planned,
            shifts = shifts, staff = staff, contextShifts = context,
            availabilitySlots = settings.definedAvailabilitySlots,
            nominalMonthlyHours = nominalHours.toDouble(),
            options = options.copy(
                firstDayOfWeek = settings.firstDayOfWeek.takeIf { it in 1..7 } ?: options.firstDayOfWeek,
                // SH-133: the roster's day warnings and rest rules, followed by auto-assign too.
                requireLeadEachDay = options.requireShiftLead && leadRule.marksAny,
                restRulesForAllContracts = settings.restRulesForAllContracts,
                // SH-221: the first roster may use people who sent no availability.
                firstRoster = options.firstRoster || isFirstRoster(roster.workplaceId)
            )
        )
        PreparedSchedule(roster.workplaceId, problem, warnings)
    }

    /**
     * SH-221: whether the workplace has no PUBLISHED or LOCKED (or ARCHIVED) roster yet. When the rosters
     * cannot be read, the usual rule applies (never without availability).
     */
    suspend fun isFirstRoster(workplaceId: String): Boolean = runCatching {
        withTimeoutOrNull(ROSTERS_TIMEOUT_MS) { rosterRepository.observeRosters(workplaceId).first() }
            ?.none { it.status == RosterStatus.PUBLISHED || it.status == RosterStatus.LOCKED || it.status == RosterStatus.ARCHIVED } ?: false
    }.getOrElse { e -> if (e is CancellationException) throw e else false }
}

private const val ROSTERS_TIMEOUT_MS = 10_000L

/** A problem read by [PlanAutoScheduleUseCase.prepare], with the warnings found while reading. */
data class PreparedSchedule(
    val workplaceId: String,
    val problem: ScheduleProblem,
    val warnings: List<ScheduleWarning>
) {
    /** Solves [problem] (with [proposals] placed first when given), the reading warnings first. */
    fun plan(proposals: List<ProposedAssignment> = emptyList()): SchedulePlan {
        val plan = AutoScheduler.plan(problem.copy(proposals = proposals))
        return plan.copy(warnings = warnings + plan.warnings)
    }
}
